<template>
  <div class="column box-content billing-plan">
    <div class="container">
      <woot-loading-state v-show="isFetchOrder" />
      <form-wizard v-show="!isFetchOrder" ref="wizard" title="" subtitle="">
        <template slot="step" scope="props" class="header-container">
          <Step :active="props.tab.active">
            {{ props.tab.title }}
          </Step>
        </template>
        <tab-content
          title="select plan"
          :before-change="() => validateStep('step-plans')"
          :lazy="true"
        >
          <Plans ref="step-plans" @on-validate="mergePartialData" />
        </tab-content>

        <tab-content
          title="select payment method"
          :before-change="onSubmit"
          :lazy="true"
        >
          <OrderConfirmation
            v-if="activeIndex === 1"
            ref="step-order-confirmation"
            title="Upgrade subscription plan"
            :orders="form.orders"
            @on-validate="mergePartialData"
            @change="handleChangeOrderConfirmation"
          />
        </tab-content>

        <tab-content title="review order" :lazy="true">
          <OrderReview
            v-if="activeIndex === 2 && orderSummary"
            ref="step-order-review"
            :order-summary="orderSummary"
            :orders="form.orders"
          />
        </tab-content>

        <template slot="footer" slot-scope="state">
          <div v-if="state.activeTabIndex > 0" class="wizard-footer">
            <div v-if="!state.isLastStep" class="row justify-space-between">
              <woot-button
                class-names="nice outline"
                color-scheme=""
                icon="ion-arrow-left-c"
                @click="state.prevTab()"
              >
                Back
              </woot-button>
              <woot-button
                :disabled="!isConfirmOrderValid"
                :is-loading="isLoadingSubmit"
                @click.native="state.nextTab()"
              >
                Confirm order
              </woot-button>
            </div>
            <div v-else>
              <div
                v-if="orderSummary.status === 'pending'"
                class="row align-right"
              >
                <woot-button
                  class-names="nice outline"
                  color-scheme=""
                  @click="handleCancelOrder(true)"
                >
                  Cancel order
                </woot-button>
                <woot-button @click="$router.go(0)">
                  Reload page
                </woot-button>
              </div>
            </div>
          </div>
          <div v-else />
        </template>
      </form-wizard>
    </div>

    <AlertModal
      :show="showModalCancelOrder"
      message="Cancel order? "
      reject-text="Cancel"
      confirm-text="Continue"
      @on-confirm="handleConfirmCancelOrderModal"
      @on-close="handleCancelOrder(false)"
    />

    <AlertModal
      :show="showModalHasPendingOrder"
      message="You already have an ongoing order waiting for payment. If you click continue, your previous order will be cancelled."
      reject-text="Cancel"
      confirm-text="Continue"
      @on-confirm="handleConfirmModalPendingOrder"
      @on-close="handleCloseModalPendingOrder"
    />
  </div>
</template>

<script>
import Plans from 'dashboard/modules/billing/components/plans/Plans';
import OrderConfirmation from 'dashboard/modules/billing/components/orders/OrderConfirmation';
import OrderReview from 'dashboard/modules/billing/components/orders/OrderReview';
import Step from 'dashboard/modules/billing/components/Step';
import AlertModal from 'dashboard/components/widgets/modal/AlertModal';
import { FormWizard, TabContent } from 'vue-form-wizard';
import WootButton from 'dashboard/components/ui/WootButton';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';

import TrialInfoAPI from 'dashboard/api/trialInfo';
import OrdersAPI from 'dashboard/api/orders';
import { mapGetters } from 'vuex';
import ordersMixin from '../../../../../shared/mixins/ordersMixin';

export default {
  name: 'BillingPlan',
  components: {
    Plans,
    OrderConfirmation,
    OrderReview,
    Step,
    FormWizard,
    TabContent,
    WootButton,
    AlertModal,
  },
  mixins: [ordersMixin],
  data() {
    return {
      form: {
        bank: '',
        orders: [],
      },
      isConfirmOrderValid: false,
      orderSummary: {},
      isLoadingSubmit: false,
      showModalCancelOrder: false,
      pendingOrder: [],
      showModalHasPendingOrder: false,
      activeIndex: 0,
      isFetchOrder: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
  },
  watch: {
    $route() {
      this.$refs.wizard.reset();
    },
  },
  activated() {
    this.init();
    this.checkHasInvoice();
    this.getPendingOrder();
  },
  methods: {
    init() {
      this.$store.dispatch('subscription/get');
      this.$watch(
        () => {
          return this.$refs.wizard.activeTabIndex;
        },
        index => {
          this.activeIndex = index;
          const withFooter = this.$el.querySelector('.with-wizard-footer');
          if (withFooter) withFooter.scrollTop = 0;
        },
      );
    },
    async getPendingOrder() {
      try {
        const req = await OrdersAPI.get({
          params: { status: 'pending', type: 'PlanOrder' },
        });
        this.pendingOrder = req.data.data;
      } catch (e) {
        this.$router.back();
      }
    },
    async checkHasInvoice() {
      try {
        this.isFetchOrder = true;
        const orderId = this.$route.query.orderId;
        if (!orderId) return;
        await this.fetchOrderReview(orderId);
        this.activeIndex = 2;
        this.$refs.wizard.changeTab(0, 2);
      } catch (e) {
        console.error(e);
      } finally {
        this.isFetchOrder = false;
      }
    },
    async fetchOrderReview(orderId) {
      try {
        const orderData = await this.getOrderId(orderId);
        const orderable = orderData.orderable;
        this.orderSummary = {
          total_amount: orderable.total_amount,
          ...orderData,
        };
        const order = this.generateOrderItem(orderData.type, orderable);
        this.form.orders = [{ ...order }];
      } catch (e) {
        bus.$emit('newToastMessage', e.message);
        await this.$router.push({ name: 'billing_credits' });
      }
    },
    generateOrderItem(type, data) {
      switch (type) {
        case 'PlanOrder':
          return this.generateOrderPlan(
            data.name,
            data.mau_quota,
            data.agent_quota,
            data.price,
          );
        case 'MauOrder':
          return this.generateOrderMau(
            data.price,
            data.sub_total,
            data.mau_quota,
          );
        default:
          return {};
      }
    },
    validateStep(refStepName) {
      const refToValidate = this.$refs[refStepName];
      return refToValidate.validate();
    },
    mergePartialData(model, isValid) {
      if (isValid) {
        this.form = { ...this.form, ...model };
      }
      this.isConfirmOrderValid = isValid;
    },
    handleChangeOrderConfirmation(bank) {
      this.form.bank = bank;
    },
    async handleCancelOrder(status) {
      this.showModalCancelOrder = status;
    },
    async handleConfirmCancelOrderModal() {
      try {
        const payload = {
          id: this.orderSummary.id,
        };
        await TrialInfoAPI.cancelOrderAccountPlanOrder(payload);
        await this.$router.push(
          `/app/accounts/${this.accountId}/settings/billing`,
        );
        this.showModalCancelOrder = false;
      } catch (e) {
        bus.$emit('newToastMessage', e.message);
      }
    },
    onSubmit() {
      if (this.pendingOrder.length > 0) {
        this.showModalHasPendingOrder = true;
        return false;
      }
      return this.doSubmitOrder();
    },
    async doSubmitOrder() {
      try {
        this.isLoadingSubmit = true;
        const isValid = this.$refs['step-order-confirmation'].validate();
        if (!isValid) throw new Error('step-order-confirmation not valid');
        const payload = {
          payment_method: this.form.bank,
          plan_id: this.form.plan.id,
        };
        const req = await TrialInfoAPI.upgradeAccountPlanOrder(payload);
        const orderId = req.data.plan_order.id;
        await this.fetchOrderReview(orderId);
        await this.$router.replace({
          query: { orderId },
        });
        this.$refs.wizard.changeTab(1, 2);
        return true;
      } catch (e) {
        bus.$emit('newToastMessage', e.message);
        return false;
      } finally {
        this.isLoadingSubmit = false;
      }
    },
    handleCloseModalPendingOrder() {
      this.showModalHasPendingOrder = false;
    },
    handleConfirmModalPendingOrder() {
      this.doSubmitOrder();
      this.showModalHasPendingOrder = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.box-content {
  overflow: auto;

  &.billing-plan {
    padding: 40px 64px;

    .container {
      overflow: hidden;
      background-color: var(--white);
      border: 1px solid #e5eaef;
      border-radius: 12px;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
<style lang="scss">
.billing-plan {
  .vue-form-wizard {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;

    .wizard-navigation {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .wizard-header,
    .wizard-card-footer {
      padding: 0;
    }

    .wizard-nav {
      &.wizard-nav-pills {
        margin: 0;
        padding: 32px 48px;
        background-color: #f5f6f8;
        display: flex !important;
        gap: 4px;
      }
    }

    .wizard-tab-content {
      overflow: auto;
      padding: 0;
      flex: 1 1 auto;
      height: 0;
    }

    .wizard-card-footer {
      .wizard-footer {
        border-top: 1px solid #eff2f5;
        padding: 24px 48px;

        .row {
          gap: 10px;
        }
      }
    }
  }
}
</style>
